import { useEffect, useState, useCallback } from 'react';
import { KanbanSquare, Loader, Plus, AlertCircle, RefreshCcw } from 'lucide-react';

import { incidentsApiService } from '../../services/incidentsApi';
import { IncidentBridgeRow } from './IncidentBridgeRow';
import { PromoteToWorkspaceModal } from './PromoteToWorkspaceModal';
import { SectionHeader } from '../ui/SectionHeader';
import { SkeletonLoader } from '../SkeletonLoader';
import type {
  IncidentKanbanLink,
  IncidentWorkspaceSummary,
} from '../../types/incident';

export interface IncidentWorkspacesSectionProps {
  incidentId: string;
  canManage: boolean;
  /**
   * Bumped by the parent when a WS event indicates the bridge state changed
   * (`incident.linked` / `incident.unlinked`). Refetches both link + summary lists.
   */
  refreshNonce?: number;
  /**
   * Called whenever the local link list is refreshed so the parent can re-evaluate
   * sync routing (`shouldUseSyncForIncident`).
   */
  onLinksChanged?: (links: IncidentKanbanLink[]) => void;
}

/**
 * Renders the "Workspaces" panel on the incident detail page: every kanban card on every
 * board that's tracking this incident, plus per-link sync flag toggles, notes, and a
 * "Promote to workspace" button to create a new bridged card on a chosen team.
 */
export const IncidentWorkspacesSection = ({
  incidentId,
  canManage,
  refreshNonce = 0,
  onLinksChanged,
}: IncidentWorkspacesSectionProps) => {
  const [summaries, setSummaries] = useState<IncidentWorkspaceSummary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPromote, setShowPromote] = useState(false);

  const fetchAll = useCallback(
    async (initial = false) => {
      if (!incidentId) return;
      if (initial) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      setError(null);
      try {
        const [links, workspaces] = await Promise.all([
          incidentsApiService.listKanbanLinks(incidentId),
          incidentsApiService.listIncidentWorkspaces(incidentId),
        ]);
        // Prefer denormalised rows when present; fall back to a synthetic row from the raw link
        // so we still surface bridge data even if the workspaces endpoint hides a board.
        const summaryByCardId = new Map(workspaces.map((w) => [w.card.id, w]));
        const merged: IncidentWorkspaceSummary[] = links.map((link) => {
          const existing = summaryByCardId.get(link.card_id);
          if (existing) return existing;
          return {
            link,
            board: { id: link.board_id, team: '', title: link.board_id, is_archived: 0 },
            card: {
              id: link.card_id,
              title: link.card_id,
              list_id: '',
              list_title: '—',
              list_role: 'none',
              is_archived: 0,
              priority: null,
              assignees_count: 0,
            },
          };
        });
        setSummaries(merged);
        onLinksChanged?.(links);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load workspaces');
      } finally {
        if (initial) setLoading(false);
        setRefreshing(false);
      }
    },
    [incidentId, onLinksChanged],
  );

  useEffect(() => {
    void fetchAll(true);
  }, [fetchAll]);

  // React to WS-driven refresh signals from the parent.
  useEffect(() => {
    if (refreshNonce === 0) return;
    void fetchAll(false);
  }, [refreshNonce, fetchAll]);

  const count = summaries?.length ?? 0;

  return (
    <div className="glass-card p-4 md:p-5">
      <SectionHeader
        as="h2"
        title={
          <span className="inline-flex items-center gap-2">
            Workspaces
            {refreshing && <Loader className="w-3.5 h-3.5 animate-spin text-glass-400" />}
          </span>
        }
        icon={KanbanSquare}
        count={count}
        description="Bridge this incident into team kanban boards. Each linked card has its own sync settings for status, priority, assignees, comments, and watchers."
        actions={
          <>
            <button
              type="button"
              onClick={() => void fetchAll(false)}
              className="btn-icon"
              title="Refresh"
              aria-label="Refresh workspaces"
              disabled={loading || refreshing}
            >
              <RefreshCcw className="w-4 h-4" />
            </button>
            {canManage && (
              <button
                type="button"
                onClick={() => setShowPromote(true)}
                className="glass-button"
              >
                <Plus className="w-4 h-4" />
                Promote to workspace
              </button>
            )}
          </>
        }
      />

      {loading && (
        <div className="space-y-2" aria-busy="true">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="subcard p-3 flex items-start gap-3">
              <SkeletonLoader className="w-4 h-4 rounded" />
              <div className="flex-1 space-y-2">
                <SkeletonLoader className="h-4 w-48 max-w-full" />
                <SkeletonLoader className="h-3 w-64 max-w-full" />
              </div>
            </div>
          ))}
        </div>
      )}

      {error && !loading && (
        <div className="alert alert-danger" role="alert">
          <AlertCircle />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && count === 0 && (
        <p className="text-sm text-glass-400">
          No workspaces tracking this incident yet.
          {canManage && ' Use "Promote to workspace" to create a kanban card on any team\u2019s board.'}
        </p>
      )}

      {!loading && !error && summaries && summaries.length > 0 && (
        <div className="space-y-2">
          {summaries.map((s) => (
            <IncidentBridgeRow
              key={s.card.id}
              incidentId={incidentId}
              summary={s}
              canManage={canManage}
              onChanged={() => void fetchAll(false)}
            />
          ))}
        </div>
      )}

      {showPromote && (
        <PromoteToWorkspaceModal
          isOpen={showPromote}
          onClose={() => setShowPromote(false)}
          incidentId={incidentId}
          onSuccess={() => {
            setShowPromote(false);
            void fetchAll(false);
          }}
        />
      )}
    </div>
  );
};
